<script setup lang="ts">
import type { ProjectLog } from '#trade-activity/queries/logs'
import { useQueryProjectLogs } from '#trade-activity/queries/logs'
import { Button } from '@forgd/ui-kit/button'
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@forgd/ui-kit/sheet'
import { PlusIcon, XMarkIcon } from '@heroicons/vue/24/outline'

// States
// - No existing logs - show add log form
// - Existing logs - show logs and "Add New Log" button
// - Editing log - show logs  with inline edit form & disable "Add New Log" button
const props = defineProps<{
  initialState: 'add' | 'edit' | 'view'
}>()

const model = defineModel<boolean>({ default: false })

const logger = useDevLogger('TaLogSlideover')

const state = ref<typeof props.initialState>(props.initialState)

const logs = useQueryProjectLogs()

const projectEditors = useProjectEditors()
const editingLog = ref<ProjectLog | null>(null)

function handleAdd() {
  state.value = 'view'
  editingLog.value = null
}

function handleEdit(log: ProjectLog) {
  state.value = 'edit'
  editingLog.value = log
}

function handleCancel() {
  state.value = 'view'
  editingLog.value = null
}

async function handleUpdate() {
  logger.debug('handleUpdate')
  state.value = 'view'
  editingLog.value = null
}

watch(() => props.initialState, (newVal) => {
  state.value = newVal
})

const totalLogs = computed(() => logs.data?.value?.total || 0)

function handleClose() {
  state.value = 'view'
  editingLog.value = null
  model.value = false
}
</script>

<template>
  <Sheet :open="model" :modal="false" @update:open="(open) => !open && handleClose()">
    <SheetContent side="right" size="sm">
      <SheetHeader>
        <div>
          <SheetTitle>Report Log</SheetTitle>
          <SheetDescription>{{ totalLogs }} {{ totalLogs === 1 ? 'Entry' : 'Entries' }}</SheetDescription>
        </div>
        <Button
          size="sm"
          variant="ghost"
          icon-only
          aria-label="Close"
          data-testid="close-slideover-button"
          @click="handleClose"
        >
          <XMarkIcon />
        </Button>
      </SheetHeader>

      <SheetBody>
        <TaLogForm
          v-if="projectEditors.isActiveEditor && state === 'add'"
          @cancel="handleCancel"
          @on-success="handleAdd"
        />
        <Button
          v-if="projectEditors.isActiveEditor"
          block
          variant="outline"
          :disabled="state === 'edit'"
          @click="state = 'add'"
        >
          Add New Log
          <PlusIcon />
        </Button>

        <div class="flex flex-col gap-2">
          <template v-for="log in logs.data?.value?.items" :key="log.id">
            <TaLogForm
              v-if="log.id === editingLog?.id"
              :log="log"
              @cancel="handleCancel"
              @on-success="handleUpdate"
            />
            <TaLogCard
              v-else
              :log="log"
              @edit="handleEdit(log)"
            />
          </template>
        </div>
      </SheetBody>
    </SheetContent>
  </Sheet>
</template>
